import { useNavigate, useParams } from "react-router";
import IApi from "../../../interfaces/IApi";
import { Avatar, Breadcrumb, Card, Col, Container, Item, List, Row, Tab, Table } from "react-dsgov";
import { useEffect, useState } from "react";
import styles from './DetalheApi.module.scss';
import classNames from "classnames";
import useCatalogoService from "../../../hooks/useCatalogoService";
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import useLoadingState from "../../../state/hooks/useLoadingState";
import useSubtituloState from "../../../state/hooks/useSubtituloState";
import { useClear } from "../../../hooks/useClear";
import { buildPageTitle } from "../../../config/portalMode";
import PageMetadata from "../../../components/PageMetadata/PageMetadata";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import useUrlBasica from "../../../hooks/useUrlBasica";

const DetalheApi: React.FC = () => {
    const navigate = useNavigate();
    const catalogoService = useCatalogoService();
    const clear = useClear();
    const urlBasica = useUrlBasica();

    const { idRotina } = useParams<{ idRotina: string }>();
    const [api, setApi] = useState<IApi>();

    const [, setLoading] = useLoadingState();
    const [, setSubtitulo] = useSubtituloState();

    useEffect(() => {
        setSubtitulo("Catálogo de APIs");
    }, [setSubtitulo]);

    useEffect(() => {
        if (!idRotina) return;

        setLoading(true);
        catalogoService.getApi(Number(idRotina)).then((api) => {
            console.log("API details fetched:", api);
            setApi(api);
            window.scrollTo(0, 0);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, [idRotina, catalogoService, setLoading]);

    useEffect(() => hljs.highlightAll(), [api]);

    useEffect(() => {
        const imagens = document.querySelectorAll<HTMLImageElement>('img');
        imagens.forEach(img => {
            if (!img.getAttribute('loading')) {
                img.setAttribute('loading', 'lazy');
            }
            if (!img.getAttribute('decoding')) {
                img.setAttribute('decoding', 'async');
            }
        });
    }, [api]);

    const renderTags = () => {
        if (!api?.tags) return null;
        return (
            <div className={styles["tags"]}>
                {api.tags.split(/[\s,;|]+/).filter(t => t).map((tag, index) => (
                    <span key={index}>#{tag}</span>
                ))}
            </div>
        );
    };

    const renderConteudoFuncional = (conteudo: string) => {
        if (/<\/?[a-z][^>]*>/i.test(conteudo)) {
            return <div className={styles["conteudo"]} dangerouslySetInnerHTML={{ __html: conteudo }} />;
        }

        const urlRegex = /(https?:\/\/[^\s<]+[^\s<.,:;"')\]}!?])/gi;
        const partes = conteudo.split(urlRegex);

        return (
            <div className={classNames(styles["conteudo"], styles["texto-simples"])}>
                {partes.map((parte, index) => (
                    /^https?:\/\//i.test(parte) ? (
                        <a key={index} href={parte} target="_blank" rel="noopener noreferrer">
                            {parte}
                        </a>
                    ) : (
                        <span key={index}>{parte}</span>
                    )
                ))}
            </div>
        );
    };

    const renderEndpoints = () => {
        if (!api?.endpoints || api.endpoints.length === 0) return null;

        return (
            <Tab.Content title="Endpoints">
                {api.endpoints.map(endpoint => (
                    <List key={endpoint.id}>
                        <Item showDividerAfter collapsable initiallyExpanded={false} subItems={<>
                            <Item>
                                <b>URL: </b> {endpoint.url}
                            </Item>
                            <Item>
                                <b>Público: </b> {endpoint.publico ? "Sim" : "Não"}
                            </Item>
                            <Item>
                                <div dangerouslySetInnerHTML={{ __html: endpoint.comentarios }}></div>
                            </Item>
                            <Item>
                                {endpoint.parametros && endpoint.parametros.length > 0 && (
                                    <>
                                        <h6>Parâmetros:</h6>
                                        <Table title="Parâmetros" showDensityButtons={false} showPageSelector={false} showPagination={false} showSearch={false} showSelectedBar={false} showTopBar={false}>
                                            <thead>
                                                <tr>
                                                    <th>Nome</th>
                                                    <th>Descrição</th>
                                                    <th>Origem</th>
                                                    <th>Valores Possíveis</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {endpoint.parametros.map(parametro => (
                                                    <tr key={parametro.id}>
                                                        <td>{parametro.nome}</td>
                                                        <td>{parametro.descricao}</td>
                                                        <td>{
                                                            {
                                                                1: "JSON",
                                                                2: "QueryString",
                                                                3: "Path"
                                                            }[parametro.origem]
                                                        }</td>
                                                        <td>{parametro.valoresPossiveis}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </>
                                )}
                            </Item>
                            <Item>
                                {endpoint.respostas && endpoint.respostas.length > 0 && (
                                    <>
                                        <h6>Respostas:</h6>
                                        <Table title="Respostas" showDensityButtons={false} showPageSelector={false} showPagination={false} showSearch={false} showSelectedBar={false} showTopBar={false}>
                                            <thead>
                                                <tr>
                                                    <th>Código</th>
                                                    <th>Descrição</th>
                                                    <th>Exemplo</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {endpoint.respostas.map(resposta => (
                                                    <tr key={resposta.id}>
                                                        <td>{resposta.codigo}</td>
                                                        <td>{resposta.descricao}</td>
                                                        <td>{resposta.exemplo && resposta.exemplo.length > 0 && <pre><code>{resposta.exemplo}</code></pre>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </>
                                )}
                            </Item>
                            <Tab>
                                {endpoint.codigoJs && <Tab.Content title="Javascript">
                                    <pre><code>{endpoint.codigoJs}</code></pre>
                                </Tab.Content>}
                                {endpoint.codigoJava && <Tab.Content title="Java">
                                    <pre><code>{endpoint.codigoJava}</code></pre>
                                </Tab.Content>}
                                {endpoint.codigoPyarialib && <Tab.Content title="Pyarialib">
                                    <pre><code>{endpoint.codigoPyarialib}</code></pre>
                                </Tab.Content>}
                                {endpoint.codigoArialibts && <Tab.Content title="Arialibts">
                                    <pre><code>{endpoint.codigoArialibts}</code></pre>
                                </Tab.Content>}
                            </Tab>
                        </>}>
                            <b><span className={classNames(styles['metodo'], styles[`metodo-${endpoint.metodo.toLowerCase()}`])}>{endpoint.metodo}</span> {endpoint.nome} v{endpoint.versao}</b>
                        </Item>
                    </List>
                ))}
            </Tab.Content>
        );
    };

    return (
        <>
            <PageMetadata title={buildPageTitle(api?.nome ? `${api.nome} | Catálogo de APIs` : "Catálogo de APIs")} />
            <Container fluid={clear} className={classNames({ "sem-margin": clear })}>
                <Breadcrumb>
                    {!clear && <Breadcrumb.Item home href="#" onClick={() => navigate("/")}></Breadcrumb.Item>}
                    <Breadcrumb.Item href="#" onClick={() => navigate("/catalogo-apis")}>
                        Catálogo de APIs
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>{api?.nome}</Breadcrumb.Item>
                </Breadcrumb>
                <Row className={classNames(styles["cabecalho"])}>
                    <Col className="d-flex flex-row align-items-center">
                        <div className={styles['avatar-api']}>
                            {api?.icone && <Avatar density="medium" icon={api.icone} bgColor={api.cor || "#1351b4"} iconColor="#FFFFFF" />}
                        </div>
                        <div>
                            <h2 className={styles["titulo"]}>{api?.nome}</h2>
                            <h4 className={styles["subtitulo"]}>{api?.subtitulo}</h4>
                        </div>
                    </Col>
                </Row>

                {api && (
                    <>
                        <div className={styles["tecnologias-categorias"]}>
                            {api.siglaSolucao && <span className={styles["tecnologia"]}><b>Solução: </b> {api.siglaSolucao}</span>}
                            {api.tecnologia && <span className={styles["tecnologia"]}><b>Tecnologia: </b> {api.tecnologia}</span>}
                            {api.publicoAlvo && <span className={styles["publico-alvo"]}><b>Público-alvo: </b> {api.publicoAlvo}</span>}
                            {api.disponibilidade && <span className={styles["disponibilidade"]}><b>Disponibilidade: </b> {api.disponibilidade}</span>}
                        </div>
                        {renderTags()}

                        <Row>
                            <Col>
                                <Tab>
                                    {api.descricao && (
                                        <Tab.Content title="Sobre">
                                            <div className={styles["conteudo"]} dangerouslySetInnerHTML={{ __html: api.descricao }} />
                                        </Tab.Content>
                                    )}
                                    {api.comoComecar && (
                                        <Tab.Content title="Como começar">
                                            <div className={styles["conteudo"]} dangerouslySetInnerHTML={{ __html: api.comoComecar }} />
                                        </Tab.Content>
                                    )}
                                    {api.detalhamentoFuncional && !api.idProjetoAria && (
                                        <Tab.Content title="Funcional">
                                            {renderConteudoFuncional(api.detalhamentoFuncional)}
                                        </Tab.Content>
                                    )}
                                    {(api.detalhamentoAcesso || api.urlProducao || api.urlSandbox) && (
                                        <Tab.Content title="Acesso">
                                            {api.detalhamentoAcesso && (
                                                <div className={styles["conteudo"]} dangerouslySetInnerHTML={{ __html: api.detalhamentoAcesso }} />
                                            )}
                                            {(api.urlProducao || api.urlSandbox) && (
                                                <div className={styles["conteudo"]}>
                                                    {api.urlProducao && (
                                                        <p>
                                                            <b>URL de produção: </b>
                                                            <a href={api.urlProducao} target="_blank" rel="noopener noreferrer">{api.urlProducao}</a>
                                                        </p>
                                                    )}
                                                    {api.urlSandbox && (
                                                        <p>
                                                            <b>URL de sandbox: </b>
                                                            <a href={api.urlSandbox} target="_blank" rel="noopener noreferrer">{api.urlSandbox}</a>
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </Tab.Content>
                                    )}
                                    {!api.idProjetoAria && renderEndpoints()}
                                    {api.idProjetoAria && (
                                        <Tab.Content title="APIs">
                                            <Tab>
                                                <Tab.Content title="SwaggerUI">
                                                    <SwaggerUI
                                                        {...(api.openapi
                                                            ? { spec: api.openapi }
                                                            : { url: api.urlSwagger || `${urlBasica}v1/portal-conhecimento/custom/openapi?projeto=${api.idProjetoAria}` })}
                                                    />
                                                </Tab.Content>
                                                {renderEndpoints()}
                                            </Tab>
                                        </Tab.Content>
                                    )}
                                </Tab>
                            </Col>
                            <Col md={3}>
                                <Card>
                                    <Card.Content>
                                        {api.linkDocumentacao && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Documentação:</b><br />
                                                <a href={api.linkDocumentacao} target="_blank" rel="noreferrer">{api.linkDocumentacao}</a>
                                            </div>
                                        )}
                                        {api.urlSwagger && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Swagger:</b><br />
                                                <a href={api.urlSwagger} target="_blank" rel="noreferrer">{api.urlSwagger}</a>
                                            </div>
                                        )}
                                        {api.urlProducao && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Produção:</b><br />
                                                <a href={api.urlProducao} target="_blank" rel="noreferrer">{api.urlProducao}</a>
                                            </div>
                                        )}
                                        {api.urlSandbox && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Sandbox:</b><br />
                                                <a href={api.urlSandbox} target="_blank" rel="noreferrer">{api.urlSandbox}</a>
                                            </div>
                                        )}
                                        {api.versao && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Versão:</b><br />
                                                {api.versao}
                                            </div>
                                        )}
                                        {api.dataVersao && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Data da versão:</b><br />
                                                {new Date(api.dataVersao).toLocaleDateString('pt-BR')}
                                            </div>
                                        )}
                                        {api.tecnologia && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Tecnologia:</b><br />
                                                {api.tecnologia}
                                            </div>
                                        )}
                                        {api.disponibilidade && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Disponibilidade:</b><br />
                                                {api.disponibilidade}
                                            </div>
                                        )}
                                        {api.publicoAlvo && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Público-alvo:</b><br />
                                                {api.publicoAlvo}
                                            </div>
                                        )}
                                        {api.controleAcesso && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Controle de acesso:</b><br />
                                                {api.controleAcesso}
                                            </div>
                                        )}
                                        {api.protocoloSeguranca && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Protocolo de segurança:</b><br />
                                                {api.protocoloSeguranca}
                                            </div>
                                        )}
                                        {api.modeloOferta && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Modelo de oferta:</b><br />
                                                {api.modeloOferta}
                                            </div>
                                        )}
                                        {api.inInternaExterna && (
                                            <div className={styles["info-lateral"]}>
                                                <b>Interna/Externa:</b><br />
                                                {api.inInternaExterna === "I" ? "Interna" : "Externa"}
                                            </div>
                                        )}
                                    </Card.Content>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}
            </Container>
        </>
    );
}

export default DetalheApi;
